import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ChatMarkdown } from './ChatPanel';
import { authFetch } from './api';
import { GoogleArtifactCard } from './CoworkPanel';
import RefineCard from './chat/RefineCard';
import PortfolioCard from './chat/PortfolioCard';
import LearnCard from './chat/LearnCard';
import ExperimentCard from './chat/ExperimentCard';
import NodeFocusCard from './chat/NodeFocusCard';
import DebateCard from './chat/DebateCard';
import GenerationFlowCard from './chat/GenerationFlowCard';
import PrototypeCard from './chat/PrototypeCard';
import PipelineCheckpointCard from './chat/PipelineCheckpointCard';
import EvolutionCard from './chat/EvolutionCard';
import PatternFlowCard from './chat/PatternFlowCard';
import { serializeTree, buildFocusedSubtree } from './treeUtils';
import './AgentChatPanel.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Quick Actions (same as ChatPanel) ────────────────────────
const QUICK_ACTIONS = {
  idea:     [
    { label: 'Find Blind Spots', prompt: 'Find blind spots in this thinking tree — missing perspectives, unstated assumptions, ignored failure modes. Be specific and actionable.' },
    { label: 'Stress Test',      prompt: 'Stress test this thinking tree. Debate the weakest points, then strengthen the tree based on what you find.' },
    { label: 'Sprint Plan',      prompt: 'Break this into a sprint plan with phases, deliverables, and effort estimates.' },
    { label: 'Deep Dive',        prompt: 'Find the most important node in this tree and drill deeper into it.' },
  ],
  codebase: [
    { label: 'Tech Debt Audit',  prompt: 'Audit this codebase for tech debt. Score severity, estimate effort, prioritize fixes.' },
    { label: 'Find Blind Spots', prompt: 'What is this analysis missing? Security gaps, untested paths, scalability risks.' },
    { label: 'Stress Test',      prompt: 'Debate the architectural decisions. What would break under 10x load?' },
    { label: 'Migration Plan',   prompt: 'Create a phased migration plan from this analysis with rollback strategies.' },
  ],
  resume:   [
    { label: 'Find Gaps',        prompt: 'Find gaps in this resume strategy — missing keywords, weak areas, positioning blind spots.' },
    { label: 'Interview Prep',   prompt: 'Create interview prep from this tree — likely questions, STAR stories, talking points.' },
    { label: 'Strengthen',       prompt: 'Run a refine loop on this resume tree to strengthen the weakest areas.' },
    { label: 'Alternatives',     prompt: 'Generate 3 alternative positioning strategies for this resume.' },
  ],
  decision: [
    { label: 'Stress Test',      prompt: 'Debate this decision tree. Challenge assumptions, find second-order effects.' },
    { label: 'Find Blind Spots', prompt: 'What\'s missing? Stakeholder perspectives, timeline risks, opportunity costs.' },
    { label: 'Score Options',    prompt: 'Score each option on clarity, feasibility, impact, and risk.' },
    { label: 'Execution Plan',   prompt: 'Create an execution plan for the recommended decision with validation phases.' },
  ],
  writing:  [
    { label: 'Find Gaps',        prompt: 'What is this writing plan missing? Counterarguments, evidence, audience segments.' },
    { label: 'Strengthen',       prompt: 'Refine this writing tree — strengthen weak arguments, add evidence.' },
    { label: 'Alternatives',     prompt: 'Generate 3 alternative structures for this piece.' },
    { label: 'Edit Checklist',   prompt: 'Create an editing checklist from this writing tree.' },
  ],
  plan:     [
    { label: 'Find Risks',       prompt: 'What risks exist in this plan? Dependencies, timeline assumptions, single points of failure.' },
    { label: 'Stress Test',      prompt: 'Stress test this plan. Debate the critical path and resource assumptions.' },
    { label: 'Score Milestones',  prompt: 'Score each milestone on feasibility, clarity, and risk.' },
    { label: 'Sprint Breakdown', prompt: 'Convert this plan into sprint-sized work packages with effort estimates.' },
  ],
  learn:    [
    { label: 'Start Learning',   prompt: '__START_LEARN__' },
    { label: 'Quiz Me',          prompt: 'Create a challenging quiz on this topic with conceptual and application questions.' },
    { label: 'Find Gaps',        prompt: 'What gaps exist in my understanding? Prerequisites, misconceptions, advanced topics.' },
    { label: 'Study Plan',       prompt: 'Break this into a structured study plan with objectives and mastery criteria.' },
  ],
};

const CHAT_MODE_CONFIG = {
  idea:     { title: 'AGENT', icon: '⚡', emptyDesc: 'Your thinking tree is loaded. I can search, modify, debate, refine, and expand it — just tell me what you need.' },
  codebase: { title: 'AGENT', icon: '⚡', emptyDesc: 'Your codebase analysis is loaded. I can drill deeper, find issues, debate architecture, and generate docs.' },
  resume:   { title: 'AGENT', icon: '⚡', emptyDesc: 'Your resume strategy is loaded. I can find gaps, strengthen nodes, generate alternatives, and prep interview materials.' },
  decision: { title: 'AGENT', icon: '⚡', emptyDesc: 'Your decision tree is loaded. I can stress test options, score trade-offs, and create execution plans.' },
  writing:  { title: 'AGENT', icon: '⚡', emptyDesc: 'Your writing analysis is loaded. I can strengthen arguments, find gaps, and generate alternative structures.' },
  plan:     { title: 'AGENT', icon: '⚡', emptyDesc: 'Your project plan is loaded. I can find risks, debate assumptions, and break it into actionable sprints.' },
  learn:    { title: 'AGENT', icon: '⚡', emptyDesc: 'Your concept tree is ready. Click "Start Learning" or ask me anything about the topic.' },
};

// Tool call display names and colors
const TOOL_CONFIG = {
  search_nodes:       { label: 'Searching nodes', color: '#6366f1' },
  add_nodes:          { label: 'Adding nodes', color: '#22c55e' },
  edit_node:          { label: 'Editing node', color: '#eab308' },
  delete_node:        { label: 'Deleting node', color: '#ef4444' },
  focus_node:         { label: 'Focusing node', color: '#8b5cf6' },
  drill_deeper:       { label: 'Drilling deeper', color: '#06b6d4' },
  debate:             { label: 'Starting debate', color: '#f97316' },
  refine:             { label: 'Running refine', color: '#14b8a6' },
  generate_portfolio: { label: 'Generating alternatives', color: '#a855f7' },
  score_nodes:        { label: 'Scoring nodes', color: '#f59e0b' },
  research_topic:     { label: 'Researching', color: '#3b82f6' },
  filter_canvas:      { label: 'Filtering canvas', color: '#64748b' },
  clear_filters:      { label: 'Clearing filters', color: '#64748b' },
  create_synapse:     { label: 'Creating connection', color: '#ec4899' },
  canvas_action:      { label: 'Running action', color: '#6366f1' },
};

// ── Tool Call Block Component ────────────────────────────────
function ToolCallBlock({ toolCall, isExpanded, onToggle }) {
  const config = TOOL_CONFIG[toolCall.name] || { label: toolCall.name, color: '#64748b' };
  const hasResult = toolCall.result !== undefined;
  const isError = toolCall.result?.error;

  return (
    <div className="agent-tool-block">
      <button className="agent-tool-header" onClick={onToggle} style={{ '--tool-color': config.color }}>
        <span className="agent-tool-icon">{hasResult ? (isError ? '✕' : '✓') : '◌'}</span>
        <span className="agent-tool-label">{config.label}</span>
        {toolCall.input && Object.keys(toolCall.input).length > 0 && (
          <span className="agent-tool-summary">
            {summarizeToolInput(toolCall.name, toolCall.input)}
          </span>
        )}
        <span className={`agent-tool-chevron ${isExpanded ? 'expanded' : ''}`}>›</span>
      </button>
      {isExpanded && (
        <div className="agent-tool-body">
          {toolCall.input && Object.keys(toolCall.input).length > 0 && (
            <div className="agent-tool-section">
              <div className="agent-tool-section-label">Input</div>
              <pre className="agent-tool-json">{JSON.stringify(toolCall.input, null, 2)}</pre>
            </div>
          )}
          {hasResult && (
            <div className="agent-tool-section">
              <div className="agent-tool-section-label">Result</div>
              <pre className={`agent-tool-json ${isError ? 'error' : ''}`}>
                {JSON.stringify(toolCall.result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function summarizeToolInput(name, input) {
  switch (name) {
    case 'search_nodes': return `"${input.query || ''}"${input.types ? ` [${input.types.join(', ')}]` : ''}`;
    case 'add_nodes': return `${input.nodes?.length || 0} node(s)`;
    case 'edit_node': return `${input.nodeId}`;
    case 'delete_node': return `${input.nodeId}${input.deleteBranch ? ' + branch' : ''}`;
    case 'focus_node': return `${input.nodeId}`;
    case 'drill_deeper': return `${input.nodeId}`;
    case 'debate': return input.nodeIds?.length ? `${input.nodeIds.length} nodes` : 'full tree';
    case 'refine': return input.nodeIds?.length ? `${input.nodeIds.length} nodes` : 'full tree';
    case 'generate_portfolio': return input.focus || 'full tree';
    case 'score_nodes': return input.nodeIds?.length ? `${input.nodeIds.length} nodes` : 'all';
    case 'research_topic': return `"${(input.query || '').slice(0, 50)}"`;
    case 'filter_canvas': return input.types?.join(', ') || input.nodeIds?.length + ' nodes' || '';
    case 'create_synapse': return `${input.source} → ${input.target}`;
    case 'canvas_action': return input.action ? Object.keys(input.action)[0] : '';
    default: return '';
  }
}

// ── Thinking Block Component ─────────────────────────────────
function ThinkingBlock({ text, isExpanded, onToggle }) {
  const tokenEstimate = Math.ceil((text || '').length / 3.5);
  return (
    <div className="agent-thinking-block">
      <button className="agent-thinking-header" onClick={onToggle}>
        <span className="agent-thinking-icon">◈</span>
        <span className="agent-thinking-label">
          Thinking{tokenEstimate > 0 ? ` (${tokenEstimate} tokens)` : ''}
        </span>
        <span className={`agent-tool-chevron ${isExpanded ? 'expanded' : ''}`}>›</span>
      </button>
      {isExpanded && (
        <div className="agent-thinking-body">
          {text}
        </div>
      )}
    </div>
  );
}

// ── Compact Boundary Component ───────────────────────────────
function CompactBoundary({ removedCount }) {
  return (
    <div className="agent-compact-boundary">
      <span className="agent-compact-line" />
      <span className="agent-compact-label">{removedCount} earlier messages compacted</span>
      <span className="agent-compact-line" />
    </div>
  );
}

// ── Helper: build novelty context payload for chat ───────────
// Compresses the engine's outputs into a token-efficient shape that the
// agentChat handler injects into its system prompt. Only emits when the
// user actually ran the novelty engine (i.e., we have data).
function buildNoveltyContext({ noveltyScores, dmadResults, recombinations, blendScores, qdArchive, noveltySummary, nodes }) {
  const scoreEntries = Object.entries(noveltyScores || {});
  if (scoreEntries.length === 0 && (!dmadResults || dmadResults.length === 0)) return null;

  // Index nodes by id so we can attach labels to the top-novel list
  const nodeById = {};
  (nodes || []).forEach(n => { nodeById[n.id] = n.label || n.data?.label; });

  const topNovel = scoreEntries
    .filter(([, v]) => typeof v.score === 'number')
    .sort((a, b) => b[1].score - a[1].score)
    .slice(0, 5)
    .map(([nodeId, v]) => ({ nodeId, label: nodeById[nodeId], score: v.score, closestExisting: v.closestExisting, whyNovel: v.whyNovel }));

  const recomb = (recombinations || []).map((r, i) => ({
    label: r.label,
    blendStrategy: r.blendStrategy,
    reasoning: r.reasoning,
    score: blendScores?.[`blend-${i}`]?.score,
    error: r.error,
  }));

  return {
    avgNovelty: noveltySummary?.avgNovelty,
    topNovel,
    recombinations: recomb,
    dmadResults: (dmadResults || []).map(d => ({
      label: d.label,
      method: d.method,
      output: d.output,
      error: d.error,
    })),
    qdStats: qdArchive?.stats,
  };
}

// ── Main AgentChatPanel Component ────────────────────────────

export default function AgentChatPanel({
  isOpen, onClose, nodes, idea, mode = 'idea', onChatAction,
  chatFilterActive, onClearFilter,
  pendingChatCards, onClearPendingCards, onCardButtonClick,
  executionStream, onStopExecution, onDismissStream,
  refineStream, portfolioStream, learnStream, experimentStream,
  debateStream, prototypeStream,
  emailContext, pipelineStages, onClosePipeline,
  focusedNode, onDismissFocus,
  patternFramework, patternExecState, onStopPattern, availablePatterns,
  sessionFileContext, pipelineCheckpoint, onPipelineCheckpointAction,
  mnemonicJobs, onGenerateVideo, onPlayVideo,
  initialChatMessages, onSaveChatMessages,
  generationFlow, sessionId, workflowOutput,
  googleArtifacts, onClickArtifact, onChatExploreDeeper, chaosLevel,
  paperTitle,
  // Phase A: Novelty engine
  noveltyScores = {}, dmadResults = [], noveltySummary = null,
  // Phase B: QD Archive + Recombination
  qdArchive = null, recombinations = [], blendScores = {},
}) {
  // ── State ──────────────────────────────────────────────────
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  // Audio lesson cache: { [conceptId]: { audioBase64, mimeType, hookLine, script } }
  const [audioCache, setAudioCache] = useState({});
  const [chatAttachedFiles, setChatAttachedFiles] = useState([]);

  // Streaming state: accumulated blocks for the current response
  const [streamBlocks, setStreamBlocks] = useState([]);
  // Track which tool blocks are expanded
  const [expandedTools, setExpandedTools] = useState(new Set());
  // Track which thinking blocks are expanded
  const [expandedThinking, setExpandedThinking] = useState(new Set());

  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const chatFileRef = useRef(null);
  const initializedRef = useRef(false);
  const messageCountAtLoadRef = useRef(0);
  const insertedMarkersRef = useRef(new Set());

  // ── Initialization & Persistence ───────────────────────────
  useEffect(() => {
    if (initialChatMessages?.length && !initializedRef.current) {
      initializedRef.current = true;
      messageCountAtLoadRef.current = initialChatMessages.length;
      setMessages(initialChatMessages);
    }
  }, [initialChatMessages]);

  useEffect(() => {
    if (!onSaveChatMessages || messages.length === 0) return;
    if (messages.length <= messageCountAtLoadRef.current) return;
    const timer = setTimeout(() => {
      const saveable = messages.filter(m =>
        m.role === 'user' || m.role === 'assistant'
      );
      if (saveable.length > 0) onSaveChatMessages(saveable);
    }, 1000);
    return () => clearTimeout(timer);
  }, [messages, onSaveChatMessages]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamBlocks]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Reset on idea change
  useEffect(() => {
    setMessages([]);
    setStreamBlocks([]);
    setInput('');
    initializedRef.current = false;
  }, [idea]);

  // Pending action cards from parent
  useEffect(() => {
    if (pendingChatCards?.length > 0) {
      setMessages(prev => [...prev, ...pendingChatCards.map(card => ({
        role: 'system',
        type: 'action_card',
        ...card,
      }))]);
      onClearPendingCards?.();
    }
  }, [pendingChatCards, onClearPendingCards]);

  // ── Live Card Markers (same pattern as ChatPanel) ──────────
  const injectMarker = useCallback((type) => {
    if (insertedMarkersRef.current.has(type)) return;
    insertedMarkersRef.current.add(type);
    setMessages(prev => [...prev, { role: 'system', type, ts: Date.now() }]);
  }, []);

  useEffect(() => { if (!pipelineStages) insertedMarkersRef.current.delete('pipeline_live'); }, [pipelineStages]);
  useEffect(() => { if (!generationFlow?.length) insertedMarkersRef.current.delete('generation_flow_live'); }, [generationFlow]);
  useEffect(() => { if (!executionStream) insertedMarkersRef.current.delete('execution_stream_live'); }, [executionStream]);
  useEffect(() => { if (!debateStream) insertedMarkersRef.current.delete('debate_live'); }, [debateStream]);
  useEffect(() => { if (!refineStream) insertedMarkersRef.current.delete('refine_live'); }, [refineStream]);
  useEffect(() => { if (!portfolioStream) insertedMarkersRef.current.delete('portfolio_live'); }, [portfolioStream]);
  useEffect(() => { if (!learnStream) insertedMarkersRef.current.delete('learn_live'); }, [learnStream]);
  useEffect(() => { if (!experimentStream) insertedMarkersRef.current.delete('experiment_live'); }, [experimentStream]);
  useEffect(() => { if (!prototypeStream) insertedMarkersRef.current.delete('prototype_live'); }, [prototypeStream]);
  useEffect(() => { if (!patternExecState) insertedMarkersRef.current.delete('pattern_live'); }, [patternExecState]);
  useEffect(() => { if (!pipelineCheckpoint) insertedMarkersRef.current.delete('pipeline_checkpoint_live'); }, [pipelineCheckpoint]);

  useEffect(() => { if (pipelineStages) injectMarker('pipeline_live'); }, [pipelineStages, injectMarker]);
  useEffect(() => { if (generationFlow?.length) injectMarker('generation_flow_live'); }, [generationFlow, injectMarker]);
  useEffect(() => { if (executionStream) injectMarker('execution_stream_live'); }, [executionStream, injectMarker]);
  useEffect(() => { if (debateStream) injectMarker('debate_live'); }, [debateStream, injectMarker]);
  useEffect(() => { if (refineStream) injectMarker('refine_live'); }, [refineStream, injectMarker]);
  useEffect(() => { if (portfolioStream) injectMarker('portfolio_live'); }, [portfolioStream, injectMarker]);
  useEffect(() => { if (learnStream) injectMarker('learn_live'); }, [learnStream, injectMarker]);
  useEffect(() => { if (experimentStream) injectMarker('experiment_live'); }, [experimentStream, injectMarker]);
  useEffect(() => { if (prototypeStream) injectMarker('prototype_live'); }, [prototypeStream, injectMarker]);
  useEffect(() => { if (patternExecState) injectMarker('pattern_live'); }, [patternExecState, injectMarker]);
  useEffect(() => { if (pipelineCheckpoint) injectMarker('pipeline_checkpoint_live'); }, [pipelineCheckpoint, injectMarker]);

  // ── Send Message (Agentic) ─────────────────────────────────
  const sendMessage = useCallback(async (content) => {
    if (!content.trim() || isStreaming) return;

    // Handle special learn mode trigger
    if (content === '__START_LEARN__') {
      onCardButtonClick?.({ actionType: 'openPanel', panel: 'learn' });
      return;
    }

    let fullContent = content.trim();
    if (chatAttachedFiles.length > 0) {
      const fileBlock = chatAttachedFiles.map(f => `[Attached: ${f.name}]\n${f.text}`).join('\n\n');
      fullContent += `\n\n--- ATTACHED FILES ---\n${fileBlock}`;
      setChatAttachedFiles([]);
    }

    const userMsg = { role: 'user', content: fullContent };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsStreaming(true);
    setStreamBlocks([]);
    setExpandedTools(new Set());

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const focusContext = buildFocusedSubtree(focusedNode, nodes);
      const treeContext = focusContext
        ? serializeTree(nodes.filter(n => !focusContext.subtree.includes(n.id)))
        : serializeTree(nodes);

      // Build messages for API — only user/assistant, with content as strings
      const apiMessages = newMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content || '' }));

      const res = await authFetch(`${API_URL}/api/agent-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: apiMessages,
          treeContext,
          treeNodes: (nodes || []).map(n => ({
            id: n.id, type: n.type, label: n.label || n.data?.label,
            reasoning: n.reasoning || n.data?.reasoning,
            parentId: n.parentId || n.data?.parentId || (n.parentIds?.[0]) || null,
          })),
          idea,
          mode,
          emailThread: emailContext?.formatted || null,
          focusedNode: focusContext,
          sessionFileContext: sessionFileContext || null,
          sessionId: sessionId || null,
          chaosLevel: chaosLevel ?? 100,
          googleArtifacts: googleArtifacts?.length ? googleArtifacts.map(ga => ({
            id: ga.id, type: ga.type, title: ga.title, url: ga.url || ga.editUrl, createdAt: ga.createdAt,
          })) : null,
          // Phase B redesign: feed novelty engine results into the chat agent's context
          // so it can answer "which blend should I bet on", "what's the hidden assumption", etc.
          noveltyContext: buildNoveltyContext({ noveltyScores, dmadResults, recombinations, blendScores, qdArchive, noveltySummary, nodes }),
          // Attached docs (PDF/DOCX/XLSX) with base64 payload. Server extracts
          // text via pdf-parse / mammoth / xlsx and appends to the user
          // message so the agent reads the file content instead of routing
          // to research_topic on the filename alone.
          attachedDocs: chatAttachedFiles
            .filter(f => f.base64)
            .map(f => ({ name: f.name, ext: f.ext, size: f.size, base64: f.base64 })),
        }),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      // Parse typed SSE events
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';
      let currentText = '';
      let currentThinking = '';
      let toolCalls = [];       // {id, name, input, result}
      let canvasActions = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') break;

          try {
            const event = JSON.parse(payload);

            switch (event.type) {
              case 'text':
                currentText += event.text;
                setStreamBlocks(buildBlocks(currentThinking, currentText, toolCalls));
                break;

              case 'thinking':
                currentThinking += event.text;
                setStreamBlocks(buildBlocks(currentThinking, currentText, toolCalls));
                break;

              case 'tool_call':
                toolCalls = [...toolCalls, { id: event.id, name: event.name, input: event.input }];
                setStreamBlocks(buildBlocks(currentThinking, currentText, toolCalls));
                // Reset text accumulator for the next turn's text
                currentText = '';
                currentThinking = '';
                break;

              case 'tool_result':
                toolCalls = toolCalls.map(tc =>
                  tc.id === event.id ? { ...tc, result: event.result } : tc
                );
                setStreamBlocks(buildBlocks(currentThinking, currentText, toolCalls));
                break;

              case 'canvas_action':
                canvasActions.push(event.action);
                // Dispatch immediately
                if (onChatAction) onChatAction(event.action);
                break;

              case 'canvas_actions_summary':
                // Already dispatched individually
                break;

              case 'compact':
                setMessages(prev => [...prev, {
                  role: 'system', type: 'compact_boundary',
                  removedCount: event.removedCount,
                }]);
                break;

              case 'usage':
                // Could display token usage — store for now
                break;

              case 'error': {
                // Parse clean error message from various formats
                let errorMsg = event.error;
                if (typeof errorMsg === 'object') {
                  errorMsg = errorMsg?.message || errorMsg?.error?.message || JSON.stringify(errorMsg);
                } else if (typeof errorMsg === 'string') {
                  try {
                    const parsed = JSON.parse(errorMsg);
                    errorMsg = parsed?.error?.message || parsed?.message || errorMsg;
                  } catch { /* use as-is */ }
                }
                currentText += `\n\n**Error:** ${errorMsg}`;
                setStreamBlocks(buildBlocks(currentThinking, currentText, toolCalls));
                break;
              }

              default:
                break;
            }
          } catch (e) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }

      // Finalize: build assistant message from accumulated blocks
      const assistantMsg = {
        role: 'assistant',
        content: currentText || '',
        thinking: currentThinking || undefined,
        toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
        canvasActions: canvasActions.length > 0 ? canvasActions : undefined,
      };
      setMessages(prev => [...prev, assistantMsg]);
      setStreamBlocks([]);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Agent chat error:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
      setStreamBlocks([]);
    } finally {
      setIsStreaming(false);
    }
  }, [messages, nodes, idea, mode, isStreaming, onChatAction, focusedNode,
      emailContext?.formatted, sessionFileContext, sessionId, chaosLevel,
      googleArtifacts, chatAttachedFiles, onCardButtonClick]);

  // Build display blocks from accumulated state
  function buildBlocks(thinking, text, toolCalls) {
    const blocks = [];
    if (thinking) blocks.push({ type: 'thinking', text: thinking, id: 'thinking_0' });
    // Interleave tool calls with text segments
    for (const tc of toolCalls) {
      blocks.push({ type: 'tool_call', ...tc });
    }
    if (text) blocks.push({ type: 'text', text, id: 'text_final' });
    return blocks;
  }

  const handleStop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    // Save whatever we've streamed so far
    if (streamBlocks.length > 0) {
      const textBlock = streamBlocks.find(b => b.type === 'text');
      const thinkingBlock = streamBlocks.find(b => b.type === 'thinking');
      const toolCallBlocks = streamBlocks.filter(b => b.type === 'tool_call');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: textBlock?.text || '(stopped)',
        thinking: thinkingBlock?.text || undefined,
        toolCalls: toolCallBlocks.length > 0 ? toolCallBlocks : undefined,
      }]);
      setStreamBlocks([]);
    }
    setIsStreaming(false);
  }, [streamBlocks]);

  // File attachment
  // Text files → read as string (existing behavior).
  // Doc binaries (PDF / DOCX / XLSX) → read as base64 so the server can
  // extract text via pdf-parse / mammoth / xlsx and inject it into the
  // user message. Previously these were stored as
  // "[Binary file: foo.pdf (123KB)]" which was the only thing the agent
  // saw — so it ignored the attachment and routed to research_topic
  // instead of reading the document.
  const handleChatFileAttach = useCallback((e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    const TEXT_EXTS = ['.txt', '.md', '.json', '.yaml', '.yml', '.js', '.ts', '.jsx', '.tsx', '.py', '.go', '.rs', '.rb', '.csv', '.sql', '.sh'];
    const DOC_EXTS = ['.pdf', '.docx', '.doc', '.xlsx', '.xls'];
    const MAX_DOC_SIZE = 30 * 1024 * 1024; // 30MB — large enough for most papers, small enough to base64 over wire
    const readers = fileList.map(file => {
      return new Promise((resolve) => {
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        // Text — embed directly
        if (TEXT_EXTS.includes(ext) && file.size < 100000) {
          const reader = new FileReader();
          reader.onload = (ev) => resolve({ name: file.name, size: file.size, text: ev.target.result?.slice(0, 50000) });
          reader.readAsText(file);
          return;
        }
        // PDF / DOCX / XLSX — capture base64 for server-side extraction
        if (DOC_EXTS.includes(ext) && file.size <= MAX_DOC_SIZE) {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const dataUrl = ev.target.result || '';
            const base64 = dataUrl.includes(',') ? dataUrl.split(',', 2)[1] : '';
            resolve({
              name: file.name,
              size: file.size,
              ext,
              base64,
              text: `[Attached document: ${file.name} — content will be extracted server-side]`,
            });
          };
          reader.readAsDataURL(file);
          return;
        }
        // Anything else — placeholder only
        resolve({ name: file.name, size: file.size, text: `[Binary file: ${file.name} (${(file.size / 1024).toFixed(1)}KB)]` });
      });
    });
    Promise.all(readers).then(files => {
      setChatAttachedFiles(prev => [...prev, ...files]);
    });
    e.target.value = '';
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }, [input, sendMessage]);

  const handleCopy = useCallback((text) => {
    navigator.clipboard?.writeText(text).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
  }, []);

  const toggleToolExpand = useCallback((id) => {
    setExpandedTools(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleThinkingExpand = useCallback((id) => {
    setExpandedThinking(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (!isOpen) return null;

  const actions = patternFramework?.quickActions?.length > 0
    ? patternFramework.quickActions
    : (QUICK_ACTIONS[mode] || QUICK_ACTIONS.idea);
  const hasTree = nodes && nodes.length > 0;

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="chat-panel agent-chat-panel">
      <div className="chat-panel-header">
        <div className="chat-panel-title">
          <span className="chat-panel-icon agent-icon">⚡</span>
          <span>AGENT</span>
          {hasTree && <span className="chat-node-count">{nodes.length} nodes</span>}
          {chatFilterActive && (
            <button className="chat-filter-badge" onClick={onClearFilter}>FILTERED ✕</button>
          )}
        </div>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>

      {/* Messages area */}
      <div className="chat-messages" ref={scrollRef}>
        {messages.length === 0 && streamBlocks.length === 0 && (
          <div className="chat-empty">
            <div className="chat-empty-icon agent-empty-icon">⚡</div>
            <div className="chat-empty-title">AGENTIC CHAT</div>
            <div className="chat-empty-desc">
              {hasTree
                ? (CHAT_MODE_CONFIG[mode] || CHAT_MODE_CONFIG.idea).emptyDesc
                : 'Generate a thinking tree first, then the agent can operate on it.'}
            </div>
            {hasTree && (
              <div className="chat-empty-actions">
                {actions.map((a, i) => (
                  <button key={i} className="chat-quick-btn" onClick={() => sendMessage(a.prompt)}>
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Render messages */}
        {messages.map((msg, idx) => {
          // System messages: live cards, action cards, compact boundaries
          if (msg.role === 'system') {
            return renderSystemMessage(msg, idx, {
              debateStream, refineStream, portfolioStream, learnStream,
              experimentStream, prototypeStream, executionStream,
              generationFlow, patternExecState, pipelineCheckpoint, pipelineStages,
              onCardButtonClick, onStopExecution, onDismissStream, onClosePipeline,
              onStopPattern, onPipelineCheckpointAction, onChatExploreDeeper,
              nodes, idea, paperTitle, audioCache,
              onCacheAudio: (cid, data) => setAudioCache(prev => ({ ...prev, [cid]: data })),
              noveltyScores, dmadResults, noveltySummary,
              qdArchive, recombinations, blendScores,
            });
          }

          // User messages
          if (msg.role === 'user') {
            return (
              <div key={idx} className="chat-msg user">
                <div className="chat-msg-label">YOU</div>
                <div className="chat-msg-content">{msg.content}</div>
              </div>
            );
          }

          // Assistant messages with typed blocks
          if (msg.role === 'assistant') {
            return (
              <div key={idx} className="chat-msg assistant">
                <div className="chat-msg-label agent-label">⚡ AGENT</div>
                {/* Thinking block */}
                {msg.thinking && (
                  <ThinkingBlock
                    text={msg.thinking}
                    isExpanded={expandedThinking.has(`msg_${idx}_thinking`)}
                    onToggle={() => toggleThinkingExpand(`msg_${idx}_thinking`)}
                  />
                )}
                {/* Tool calls */}
                {msg.toolCalls?.map((tc, tcIdx) => (
                  <ToolCallBlock
                    key={tc.id || tcIdx}
                    toolCall={tc}
                    isExpanded={expandedTools.has(tc.id)}
                    onToggle={() => toggleToolExpand(tc.id)}
                  />
                ))}
                {/* Text content */}
                {msg.content && (
                  <div className="chat-msg-content">
                    <ChatMarkdown content={msg.content} />
                  </div>
                )}
                {/* Action buttons */}
                <div className="chat-msg-actions">
                  {msg.content && (
                    <button className="chat-copy-btn" onClick={() => handleCopy(msg.content)}>Copy</button>
                  )}
                </div>
              </div>
            );
          }

          return null;
        })}

        {/* Streaming blocks (current response) */}
        {streamBlocks.length > 0 && (
          <div className="chat-msg assistant streaming">
            <div className="chat-msg-label agent-label">⚡ AGENT</div>
            {streamBlocks.map((block, bIdx) => {
              if (block.type === 'thinking') {
                return (
                  <ThinkingBlock
                    key={bIdx}
                    text={block.text}
                    isExpanded={expandedThinking.has(block.id)}
                    onToggle={() => toggleThinkingExpand(block.id)}
                  />
                );
              }
              if (block.type === 'tool_call') {
                return (
                  <ToolCallBlock
                    key={block.id || bIdx}
                    toolCall={block}
                    isExpanded={expandedTools.has(block.id)}
                    onToggle={() => toggleToolExpand(block.id)}
                  />
                );
              }
              if (block.type === 'text') {
                return (
                  <div key={bIdx} className="chat-msg-content">
                    <ChatMarkdown content={block.text} />
                    <span className="agent-cursor">▊</span>
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}

        {/* Streaming indicator with no blocks yet */}
        {isStreaming && streamBlocks.length === 0 && (
          <div className="chat-msg assistant streaming">
            <div className="chat-msg-label agent-label">⚡ AGENT</div>
            <div className="agent-thinking-indicator">
              <span className="agent-dot" />
              <span className="agent-dot" />
              <span className="agent-dot" />
            </div>
          </div>
        )}

        {/* Google artifacts */}
        {googleArtifacts?.length > 0 && (
          <div className="chat-google-artifacts">
            {googleArtifacts.map(a => (
              <GoogleArtifactCard key={a.id} artifact={a} onClick={() => onClickArtifact?.(a.id)} />
            ))}
          </div>
        )}

        {/* Node focus card */}
        {focusedNode && (
          <NodeFocusCard
            node={focusedNode.node}
            onDismiss={onDismissFocus}
            onAction={onCardButtonClick}
            surgicalExpanded={focusedNode.surgicalExpanded}
            isSplitting={focusedNode.isSplitting}
            isMerging={focusedNode.isMerging}
            mergeTarget={focusedNode.mergeTarget}
            availablePatterns={availablePatterns}
          />
        )}
      </div>

      {/* Quick action bar (after first message) */}
      {messages.length > 0 && hasTree && (
        <div className="chat-quick-bar">
          {actions.map((a, i) => (
            <button key={i} className="chat-quick-chip" onClick={() => sendMessage(a.prompt)} disabled={isStreaming}>
              {a.label}
            </button>
          ))}
        </div>
      )}

      {/* Attached files preview */}
      {chatAttachedFiles.length > 0 && (
        <div className="chat-attached-files">
          {chatAttachedFiles.map((f, i) => (
            <span key={i} className="chat-attached-chip">
              📎 {f.name}
              <button onClick={() => setChatAttachedFiles(prev => prev.filter((_, j) => j !== i))}>✕</button>
            </span>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="chat-input-area">
        <button className="chat-attach-btn" onClick={() => chatFileRef.current?.click()} title="Attach file" disabled={isStreaming}>
          📎
        </button>
        <input type="file" ref={chatFileRef} style={{ display: 'none' }} multiple onChange={handleChatFileAttach} />
        <textarea
          ref={inputRef}
          className="chat-input"
          placeholder={isStreaming ? 'Agent is working...' : 'Tell the agent what to do...'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
          rows={1}
          style={{ minHeight: '40px', maxHeight: '120px', resize: 'none' }}
        />
        {isStreaming ? (
          <button className="chat-send-btn stop" onClick={handleStop}>⬛</button>
        ) : (
          <button className="chat-send-btn" onClick={() => sendMessage(input)} disabled={!input.trim()}>↑</button>
        )}
      </div>
    </div>
  );
}

// ── System Message Renderer ──────────────────────────────────
// Renders live cards, action cards, and compact boundaries.
function renderSystemMessage(msg, idx, props) {
  const {
    debateStream, refineStream, portfolioStream, learnStream,
    experimentStream, prototypeStream, executionStream,
    generationFlow, patternExecState, pipelineCheckpoint, pipelineStages,
    onCardButtonClick, onStopExecution, onDismissStream, onClosePipeline,
    onStopPattern, onPipelineCheckpointAction, onChatExploreDeeper,
    // Audio lesson props
    nodes, idea, paperTitle, audioCache, onCacheAudio,
    // Phase A: Novelty engine
    noveltyScores, dmadResults, noveltySummary,
    // Phase B: QD Archive + Recombination
    qdArchive, recombinations, blendScores,
  } = props;

  // Compact boundary
  if (msg.type === 'compact_boundary') {
    return <CompactBoundary key={idx} removedCount={msg.removedCount} />;
  }

  // Live cards (same rendering as ChatPanel)
  if (msg.type === 'debate_live' && debateStream) {
    return <DebateCard key={idx} {...debateStream} onAction={onCardButtonClick} />;
  }
  if (msg.type === 'refine_live' && refineStream) {
    return <RefineCard key={idx} {...refineStream} onAction={onCardButtonClick} />;
  }
  if (msg.type === 'portfolio_live' && portfolioStream) {
    return <PortfolioCard key={idx} {...portfolioStream} onAction={onCardButtonClick} />;
  }
  if (msg.type === 'learn_live' && learnStream) {
    return <LearnCard key={idx} {...learnStream} onAction={onCardButtonClick} onExploreDeeper={onChatExploreDeeper}
      nodes={nodes} topic={idea} paperTitle={paperTitle}
      audioCache={audioCache} onCacheAudio={onCacheAudio} />;
  }
  if (msg.type === 'experiment_live' && experimentStream) {
    return <ExperimentCard key={idx} {...experimentStream} onAction={onCardButtonClick} />;
  }
  if (msg.type === 'prototype_live' && prototypeStream) {
    return <PrototypeCard key={idx} {...prototypeStream} onAction={onCardButtonClick} />;
  }
  if (msg.type === 'generation_flow_live' && generationFlow?.length) {
    return (
      <GenerationFlowCard
        key={idx}
        stages={generationFlow}
        noveltyScores={noveltyScores}
        dmadResults={dmadResults}
        noveltySummary={noveltySummary}
        qdArchive={qdArchive}
        recombinations={recombinations}
        blendScores={blendScores}
      />
    );
  }
  if (msg.type === 'pattern_live' && patternExecState) {
    return <PatternFlowCard key={idx} {...patternExecState} onStop={onStopPattern} />;
  }
  if (msg.type === 'pipeline_checkpoint_live' && pipelineCheckpoint) {
    return <PipelineCheckpointCard key={idx} checkpoint={pipelineCheckpoint} onAction={onPipelineCheckpointAction} />;
  }

  // Saved cards from previous sessions
  if (msg.type === 'refine_card') return <RefineCard key={idx} {...msg.state} onAction={onCardButtonClick} />;
  if (msg.type === 'portfolio_card') return <PortfolioCard key={idx} {...msg.state} onAction={onCardButtonClick} />;
  if (msg.type === 'debate_card') return <DebateCard key={idx} {...msg.state} onAction={onCardButtonClick} />;
  if (msg.type === 'experiment_card') return <ExperimentCard key={idx} {...msg.state} onAction={onCardButtonClick} />;
  if (msg.type === 'evolution') return <EvolutionCard key={idx} taskId={msg.taskId} plan={msg.plan} history={msg.evolutionHistory} />;

  // Action card
  if (msg.type === 'action_card') {
    return (
      <div key={idx} className="chat-action-card">
        <div className="chat-action-card-label">{msg.label}</div>
        {msg.detail && <div className="chat-action-card-detail">{msg.detail}</div>}
        {msg.buttons?.map((btn, bi) => (
          <button key={bi} className="chat-action-card-btn" onClick={() => onCardButtonClick?.(btn)}>
            {btn.label}
          </button>
        ))}
      </div>
    );
  }

  return null;
}
